import { FaqBindTypeEnum } from '@/store/business';

import iAxios from '../IAxios';
import api from './api';
import type {
  BindReplyCardToFaqReq,
  BindReplyCardToFaqRsp,
  CreateFaqData,
  ExportFaqsReq,
  ExportFaqsRsp,
  ListReplyCardReq,
  ListReplyCardRsp,
  SaveReplyCardReq,
  SaveReplyCardRsp,
  SetReplyCardStatusReq,
  SetReplyCardStatusRsp,
  SyncFaqsReq,
  SyncFaqsRes,
  UpdateReplyCardToClientReq,
  UpdateReplyCardToClientRsp,
} from './faqType';

/**
 * 查询 H5 自定义(如: 公告、banner 等)配置列表
 */
export const queryCustomH5List = (params) =>
  iAxios.request({
    url: api.getCustomH5List,
    method: 'get',
    params,
  });

/**
 * 保存 H5 自定义(如: 公告、banner 等)配置
 * @param {*} data
 * @returns
 */
export const saveCustomH5 = (data) =>
  iAxios.request({
    url: api.saveCustomH5,
    method: 'post',
    data,
  });

/**
 * 查询 faq 关联类型
 * @returns
 */
export const queryFaqLevelConfig = (data) =>
  iAxios.request<{ type: FaqBindTypeEnum; id: string }>({
    url: api.queryFaqLevelConfig,
    method: 'post',
    data,
  });

/**
 * 保存 faq 关联类型
 * @returns
 */
export const saveFaqLevelConfig = (data) =>
  iAxios.request<{ type: FaqBindTypeEnum }>({
    url: api.saveFaqLevelConfig,
    method: 'post',
    data,
  });

/**
 * 查询 faq 列表
 * @returns
 */
export const queryFaqList = (data) =>
  iAxios.request({
    url: api.GetFaqList,
    method: 'post',
    data,
  });

/**
 * 查询 Faq 操作记录列表
 * @returns
 */
export const queryFaqOperateRecords = (data) =>
  iAxios.request({
    url: api.GetFaqOperateRecords,
    method: 'post',
    data,
  });

/**
 * 修改 Faq 是否为热门状态 - 返回值为一个空对象
 * @returns
 */
export const operateFaqIsHot = (data) =>
  iAxios.request({
    url: api.SetFaqHot,
    method: 'post',
    data,
  });

/**
 * 修改 Faq 是否启动状态 - 修改成功返回值为: { bind_level: '' }
 * @returns
 */
export const operateFaqStatus = (data) =>
  iAxios.request<Record<string, string>>({
    url: api.SetFaqStatus,
    method: 'post',
    data,
  });

/**
 * 新建 Faq - 返回值为: { group_id: string; is_repeated: boolean }
 * @returns
 */
export const createFaq = (data: CreateFaqData) =>
  iAxios.request({
    url: api.GetFaqCreate,
    method: 'post',
    data,
  });

/**
 * 编辑 Faq 详情 - 返回值为: { group_id: string; is_repeated: boolean }
 * @param {faq.CreateFaqData} data
 * @returns
 */
export const editFaq = (data: CreateFaqData) =>
  iAxios.request({
    url: api.GetFaqModify,
    method: 'post',
    data,
  });

/**
 * 批量上传 Faq
 * @returns
 */
export const uploadFaqByFile = (data) =>
  iAxios.request({
    url: api.faqUpload,
    method: 'post',
    data,
  });

/**
 * 编辑 Faq 分组 - 返回值为: { group_id: string; is_repeated: boolean }
 * @returns
 */
export const editFaqGroup = (data) =>
  iAxios.request({
    url: api.GetGroupModify,
    method: 'post',
    data,
  });

/**
 * 新建 Faq 分组 - 返回值为: { group_id: string; is_repeated: boolean }
 * @param {faq.EditGroupData} data
 * @returns
 */
export const createFaqGroup = (data) =>
  iAxios.request({
    url: api.GetGroupCreate,
    method: 'post',
    data,
  });

/**
 * 修改 Faq 分组状态: 删除/启用
 * @returns
 */
export const operateFaqGroupStatus = (data) =>
  iAxios.request({
    url: api.SetGroupStatus,
    method: 'post',
    data,
  });

/**
 * 导出 FAQ 列表
 * @param data 导出请求参数
 * @returns 返回导出文件的 COS URL
 */
export const exportFaqs = (data: ExportFaqsReq) =>
  iAxios.request<ExportFaqsRsp>({
    url: api.ExportFaqs,
    method: 'post',
    data,
  });

/**
 * 批量同步 FAQ 到其他项目
 */
export const syncFaqs = (data: SyncFaqsReq) =>
  iAxios.request<SyncFaqsRes>({
    url: api.SyncFaqs,
    method: 'post',
    data,
  });

/**
 * 保存回复卡片（新增或更新）
 */
export const saveReplyCard = (data: SaveReplyCardReq) =>
  iAxios.request<SaveReplyCardRsp>({
    url: api.SaveReplyCard,
    method: 'post',
    data,
  });

/**
 * 查询回复卡片列表
 */
export const listReplyCard = (data: ListReplyCardReq) =>
  iAxios.request<ListReplyCardRsp>({
    url: api.ListReplyCard,
    method: 'post',
    data,
  });

/**
 * 绑定回复卡片到 FAQ
 */
export const bindReplyCardToFaq = (data: BindReplyCardToFaqReq) =>
  iAxios.request<BindReplyCardToFaqRsp>({
    url: api.BindReplyCardToFaq,
    method: 'post',
    data,
  });

/**
 * 设置回复卡片状态
 */
export const setReplyCardStatus = (data: SetReplyCardStatusReq) =>
  iAxios.request<SetReplyCardStatusRsp>({
    url: api.SetReplyCardStatus,
    method: 'post',
    data,
  });

/**
 * 应用到C端
 */
export const updateReplyCardToClient = (data: UpdateReplyCardToClientReq) =>
  iAxios.request<UpdateReplyCardToClientRsp>({
    url: api.UpdateReplyCardToClient,
    method: 'post',
    data,
  });
